import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { auditKeys } from "@/lib/query";

export interface FieldChange {
  field: string;
  before: unknown;
  after: unknown;
}

export interface AuditLogEntry {
  id: string;
  entityType: string;
  entityId: string;
  action: "created" | "updated" | "deleted";
  actorUserId: string | null;
  actorName: string | null;
  /** Set when an admin wrote this while viewing the app as someone else: that person's name. */
  impersonatedName: string | null;
  /** For "updated": a FieldChange[]. For "created": the after snapshot. For "deleted": null. */
  diff: unknown;
  /** Why the change was made (resolved reason name); "(deleted)" if the reason was removed; null when none was recorded. */
  changeReasonName: string | null;
  /** Set for effective-dated changes (deferred edits); null otherwise. ISO from JSON. */
  effectiveDate: string | null;
  createdAt: string; // ISO from JSON
}

interface AuditLogResponse {
  data: AuditLogEntry[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

/**
 * Fetches the activity log for one entity, newest first, page by page. Each
 * page holds up to 50 entries (`limit` overrides); `fetchNextPage` walks the
 * cursor toward older entries so long histories are fully reachable. The
 * `ActivityLog` component flattens `data.pages` and renders a "Load more"
 * control while `hasNextPage` is true.
 */
export function useAuditLog(entityType: string, entityId: string, opts: { limit?: number } = {}) {
  return useInfiniteQuery({
    queryKey: auditKeys.entity(entityType, entityId),
    queryFn: async ({ pageParam }): Promise<AuditLogResponse> => {
      const params = new URLSearchParams();
      params.set("entityType", entityType);
      params.set("entityId", entityId);
      if (opts.limit) params.set("limit", String(opts.limit));
      if (pageParam) {
        params.set("cursor", pageParam);
        params.set("direction", "next");
      }
      const res = await fetch(`/api/audit-log?${params}`);
      if (!res.ok) throw new Error("Failed to fetch activity log");
      return res.json();
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.hasNext ? last.nextCursor : undefined),
  });
}

export interface GlobalAuditEntry extends AuditLogEntry {
  /** Current display name of the audited record; null when deleted or unnamed. */
  entityName: string | null;
}

interface GlobalAuditResponse {
  data: GlobalAuditEntry[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

export interface GlobalAuditFilters {
  entityTypes: string[];
  actions: string[];
  /** Empty string = any actor. */
  actorUserId: string;
  /** ISO instants; empty string = unbounded. `to` is exclusive. */
  from: string;
  to: string;
}

interface PageRequest {
  cursor: string | null;
  direction: "next" | "prev";
}

export const GLOBAL_AUDIT_PAGE_SIZE = 25;

/** App-wide audit log for Settings → Audit log. Admin-only endpoint. */
export function useGlobalAuditLog(filters: GlobalAuditFilters, pageRequest: PageRequest) {
  return useQuery({
    queryKey: [...auditKeys.global(filters), pageRequest] as const,
    queryFn: async (): Promise<GlobalAuditResponse> => {
      const params = new URLSearchParams();
      params.set("limit", String(GLOBAL_AUDIT_PAGE_SIZE));
      if (pageRequest.cursor) {
        params.set("cursor", pageRequest.cursor);
        params.set("direction", pageRequest.direction);
      }
      if (filters.entityTypes.length) params.set("entityType", filters.entityTypes.join(","));
      if (filters.actions.length) params.set("action", filters.actions.join(","));
      if (filters.actorUserId) params.set("actor", filters.actorUserId);
      if (filters.from) params.set("from", filters.from);
      if (filters.to) params.set("to", filters.to);
      const res = await fetch(`/api/admin/audit-log?${params}`);
      if (!res.ok) throw new Error("Failed to fetch audit log");
      return res.json();
    },
  });
}

/**
 * Download the audit log as CSV for the current filters (all matching rows,
 * not one page; capped server-side). Admin-only. Streams the response to a
 * blob and triggers a browser download using the server-supplied filename.
 */
export function useExportGlobalAuditLog() {
  return useMutation({
    mutationFn: async (filters: GlobalAuditFilters) => {
      const params = new URLSearchParams();
      if (filters.entityTypes.length) params.set("entityType", filters.entityTypes.join(","));
      if (filters.actions.length) params.set("action", filters.actions.join(","));
      if (filters.actorUserId) params.set("actor", filters.actorUserId);
      if (filters.from) params.set("from", filters.from);
      if (filters.to) params.set("to", filters.to);
      const res = await fetch(`/api/admin/audit-log/export?${params}`);
      if (!res.ok) throw new Error("Export failed");

      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition") ?? "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match?.[1] ?? "audit-log.csv";

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => toast.success("Audit log exported"),
    onError: () => toast.error("Failed to export the audit log"),
  });
}

/** Distinct entity types present in the audit log — drives the type filter. */
export function useAuditEntityTypes() {
  return useQuery({
    queryKey: auditKeys.entityTypes,
    queryFn: async (): Promise<string[]> => {
      const res = await fetch("/api/admin/audit-log/entity-types");
      if (!res.ok) throw new Error("Failed to fetch audit entity types");
      const json = await res.json();
      return json.data;
    },
  });
}
